import { all, call, fork, put, takeEvery, takeLatest } from "redux-saga/effects";
import {
  CARREGA_MEU_CONTEXTO,
  BUSCA_CLIENTES_SELECIONAVEIS,
  SELECIONA_CLIENTE_SEGURANCA,
  LIMPA_CLIENTE_ATIVO_SEGURANCA,
  SELECIONA_MODULOS,
  SELECIONA_MODULOS_PORTARIA,
} from "constants/ActionTypes";
import requisicaoApiSeguranca from 'util/ApiSeguranca';
import { userSignOut, showAuthMessage } from "actions/Auth";
import {
  carregaMeuContextoSucesso,
  carregaMeuContextoErro,
  clientesSelecionaveisSucesso,
  fechaTrocaCliente,
} from "actions/Seguranca";

/*#################################################################################################################*/
/* --------------------------------------------- Requests ---------------------------------------------------------*/

const getMeuContexto = async () =>
  await requisicaoApiSeguranca.get('/me')
    .then(resposta => resposta)
    .catch(error => error);

const getClientesSelecionaveis = async (busca) =>
  await requisicaoApiSeguranca.get('/me/clientes', { params: busca ? { busca } : {} })
    .then(resposta => resposta)
    .catch(error => error);

const postSelecionaCliente = async (clienteId) =>
  await requisicaoApiSeguranca.post(`/me/clientes/${clienteId}/selecionar`)
    .then(resposta => resposta)
    .catch(error => error);

const deleteClienteAtivo = async () =>
  await requisicaoApiSeguranca.delete('/me/cliente')
    .then(resposta => resposta)
    .catch(error => error);

/*#################################################################################################################*/
/* --------------------------------------------------- Functions --------------------------------------------------*/
/*#################################################################################################################*/

// Sempre mostra a tela de seleção de módulos após o login (AlternaModulos), que já filtra
// pra exibir só os módulos que o usuário realmente tem (ver seguranca.modulos/portaria no
// próprio componente) — decisão do produto: usuário sempre escolhe o módulo, mesmo tendo só
// um disponível. loginRecente:true esconde o botão "Fechar" nesse primeiro carregamento,
// forçando a escolha (mesmo mecanismo que já existia pro caso portaria+backoffice).
//
// Antes essa decisão (e o cálculo de acessaPortaria/acessaBackOffice) estava duplicada em
// actions/Auth.js (userSignInSuccess) e de novo em reducers/Auth.js (SIGNIN_USER_SUCCESS),
// cada um calculando o mesmo booleano separadamente a partir de "configuracao". Consolidei
// num lugar só, despachando as actions primitivas (SELECIONA_MODULOS,
// SELECIONA_MODULOS_PORTARIA) que os reducers de Auth/Settings já sabem tratar.
function decideRoteamentoModulo() {
  return [
    { type: SELECIONA_MODULOS },
    { type: SELECIONA_MODULOS_PORTARIA, payload: { configuracao: {}, loginRecente: true } },
  ];
}

// Substitui a derivação que antes acontecia no login (sagas/Auth.js) a partir de
// "configuracao.listaConfiguracaoPortalFuncionalideAcao". Agora usa a lista achatada
// "permissoes" (funcionalidade+ação) de GET /me. CADSIMPLIFPERFIL virou dois códigos na
// Uzerpass.Seguranca (o legado tinha o mesmo código em duas funcionalidades diferentes —
// ver docs/modulos-menus-seed.md) — testa os dois pra manter o comportamento antigo.
function atualizaFlagsCadastroSimplificado(permissoes) {
  const lista = permissoes || [];
  const temPermissao = (funcionalidade, acao) => lista.some(
    p => p.funcionalidade === funcionalidade && (!acao || p.acao === acao)
  );

  const cadSimplifPerfil = temPermissao('CADSIMPLIFPERFIL') || temPermissao('CADSIMPLIFVOUCHER');
  const cadSimplifInsert = temPermissao('PESSCONTTEND', 'INSERIR');
  const cadSimplifRemove = temPermissao('PESSCONTTEND', 'EXCLUIR');

  localStorage.setItem('cad_simplif_perfil', cadSimplifPerfil);
  localStorage.setItem('cad_simplif_insert', cadSimplifInsert);
  localStorage.setItem('cad_simplif_remove', cadSimplifRemove);
}

function* fnCarregaMeuContexto({ payload }) {
  try {
    const retorno = yield call(getMeuContexto);

    if (!retorno.isAxiosError && retorno.data) {
      atualizaFlagsCadastroSimplificado(retorno.data.permissoes);
      yield put(carregaMeuContextoSucesso(retorno.data));

      if (payload && payload.decideModulo) {
        const acoes = decideRoteamentoModulo();
        for (const acao of acoes) {
          yield put(acao);
        }
      }
    }
    else if (retorno.response && retorno.response.status === 401) {
      yield put(userSignOut());
    }
    else {
      yield put(carregaMeuContextoErro('Ocorreu um erro ao carregar módulos/permissões'));
    }
  } catch (error) {
    yield put(carregaMeuContextoErro(error));
  }
}

function* fnBuscaClientesSelecionaveis({ payload }) {
  try {
    const retorno = yield call(getClientesSelecionaveis, payload.busca);

    if (!retorno.isAxiosError && retorno.data) {
      yield put(clientesSelecionaveisSucesso(retorno.data));
    }
    else {
      yield put(showAuthMessage('Ocorreu um erro ao listar clientes'));
    }
  } catch (error) {
    yield put(showAuthMessage(error));
  }
}

function* fnSelecionaCliente({ payload }) {
  try {
    const retorno = yield call(postSelecionaCliente, payload.clienteId);

    if (!retorno.isAxiosError && retorno.data) {
      yield put(carregaMeuContextoSucesso(retorno.data));
      yield put(fechaTrocaCliente());
    }
    else {
      yield put(showAuthMessage('Não foi possível trocar de cliente'));
    }
  } catch (error) {
    yield put(showAuthMessage(error));
  }
}

function* fnLimpaClienteAtivo() {
  try {
    const retorno = yield call(deleteClienteAtivo);

    if (!retorno.isAxiosError && retorno.data) {
      yield put(carregaMeuContextoSucesso(retorno.data));
    }
    else {
      yield put(showAuthMessage('Não foi possível limpar o cliente ativo'));
    }
  } catch (error) {
    yield put(showAuthMessage(error));
  }
}

/*#################################################################################################################*/
/* -------------------------------------------- Chamadas de Funções -----------------------------------------------*/
/*#################################################################################################################*/

export function* carregaMeuContexto() {
  yield takeEvery(CARREGA_MEU_CONTEXTO, fnCarregaMeuContexto);
}

export function* buscaClientesSelecionaveis() {
  yield takeLatest(BUSCA_CLIENTES_SELECIONAVEIS, fnBuscaClientesSelecionaveis);
}

export function* selecionaClienteSeguranca() {
  yield takeEvery(SELECIONA_CLIENTE_SEGURANCA, fnSelecionaCliente);
}

export function* limpaClienteAtivoSeguranca() {
  yield takeEvery(LIMPA_CLIENTE_ATIVO_SEGURANCA, fnLimpaClienteAtivo);
}

export default function* rootSaga() {
  yield all([
    fork(carregaMeuContexto),
    fork(buscaClientesSelecionaveis),
    fork(selecionaClienteSeguranca),
    fork(limpaClienteAtivoSeguranca),
  ]);
}
